import { motion } from "framer-motion";
import { Mic, ScanLine, Keyboard, Sparkles } from "lucide-react";

export default function Trimodal() {
  return (
    <section
      id="producto"
      className="relative py-24 md:py-32 bg-[#080808]"
      data-testid="trimodal"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase">
              / Cómo tú prefieras
            </p>
          </div>
          <div className="md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]"
            >
              Tres formas de vender.<br />
              <span className="text-white/40">Una sola tienda.</span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* Voice — main */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-8 rounded-3xl border border-white/10 bg-gradient-to-br from-[#ff5a00]/[0.12] to-transparent p-8 md:p-12 min-h-[420px] relative overflow-hidden"
            data-testid="trimodal-voice"
          >
            <div className="absolute top-6 right-6 chip">
              <Sparkles size={12} /> Principal
            </div>
            <Mic size={44} strokeWidth={1.5} className="text-[#ff5a00]" />
            <h3 className="font-display text-5xl md:text-6xl tracking-tighter mt-8 leading-none">
              Voz IA
            </h3>
            <p className="text-white/70 mt-4 max-w-md text-[15px] leading-relaxed">
              Presiona el micrófono. Habla como siempre. Gemini 3 Pro convierte tu voz en ventas, altas de inventario, consultas o cortes de caja — en tiempo real.
            </p>

            <div className="mt-10 flex flex-wrap gap-2">
              {[
                "cobra 2 refrescos",
                "agrega 3 cajas de Corona",
                "¿cuánto llevo hoy?",
                "cierra caja",
              ].map((chip, i) => (
                <span key={i} className="chip">{chip}</span>
              ))}
            </div>

            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#ff5a00]/20 blur-3xl pointer-events-none" />
          </motion.div>

          {/* QR */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4 rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-10 min-h-[420px] relative overflow-hidden"
            data-testid="trimodal-qr"
          >
            <ScanLine size={38} strokeWidth={1.5} className="text-[#ccff00]" />
            <h3 className="font-display text-4xl md:text-5xl tracking-tighter mt-6 leading-none">
              Escáner QR
            </h3>
            <p className="text-white/60 mt-3 text-[14px] leading-relaxed">
              Para productos empaquetados con código de barras. Escaneo instantáneo desde la cámara del celular.
            </p>
            <div className="mt-8 aspect-square w-full max-w-[220px] mx-auto rounded-2xl border border-white/10 bg-black/60 relative overflow-hidden">
              <div className="absolute inset-6 grid grid-cols-8 gap-[2px]">
                {Array.from({ length: 64 }).map((_, i) => (
                  <span
                    key={i}
                    className="rounded-[1px]"
                    style={{
                      background:
                        [0, 2, 5, 7, 9, 12, 15, 17, 20, 24, 28, 30, 33, 36, 40, 44, 47, 50, 54, 58, 61].includes(i)
                          ? "#ccff00"
                          : Math.random() > 0.5
                          ? "#f5f5f5"
                          : "transparent",
                    }}
                  />
                ))}
              </div>
              <div className="absolute inset-x-6 top-1/2 h-[2px] bg-[#ccff00] shadow-[0_0_16px_#ccff00]" />
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-12 rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-10 relative overflow-hidden"
            data-testid="trimodal-text"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <div className="flex items-center gap-3">
                  <Keyboard size={30} strokeWidth={1.5} className="text-white/70" />
                  <span className="chip">Respaldo</span>
                </div>
                <h3 className="font-display text-4xl md:text-5xl tracking-tighter mt-4 leading-none">
                  Texto — cuando el ruido no ayuda.
                </h3>
                <p className="text-white/60 mt-3 max-w-lg text-[15px] leading-relaxed">
                  ¿La fonda está a tope de gente? Escribe el comando y CHALANN entiende igual. Cero fricción, cero teclas cuando puedas, todas las teclas cuando quieras.
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="rounded-xl bg-black/50 border border-white/10 font-mono text-[13px] p-4">
                  <p className="text-white/40 text-[10px] uppercase tracking-widest mb-2">→ input</p>
                  <p className="text-white/90">cobra 3 tacos pastor, 2 aguas frescas, dame total</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-widest mt-4 mb-2">← output</p>
                  <p className="text-[#ccff00]">$174.00 · ticket #4218 · listo para cobrar</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
